import { useState, useEffect, useContext, useMemo } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'

import GiftcardEditor from './pass-editor-gift'
import EventTicketEditor from './pass-editor-event'
import StampcardEditor from './pass-editor-stamp'
import LoyaltyEditor from './pass-editor-loyalty'
import MembershipEditor from './pass-editor-membership'

import { AppContext } from '/providers/app-context'
import { getPass } from '/providers/passes-api'

import ManagePassSubpageLayout from '/components/passes/manage-pass-subpage-layout'
import HelpText from '/help/pass-scan-actions'

const PassScanActions = () => {

  const navigate = useNavigate()
  const location = useLocation()
  const { templateId, passId } = useParams()

  const { apiSource, apiKey, templates, setPasses } = useContext(AppContext)

  // Passes in the list are deliberately lightweight and do not necessarily
  // contain the scanner variables required by the action editors. Always load
  // the complete pass before mounting a type-specific editor.
  const [pass, setPass] = useState()
  const template = useMemo(() => { return templates.filter(t => t.template_id === templateId)[0] }, [templates, templateId])

  useEffect(() => {
    let active = true

    const loadPass = async () => {
      const result = await getPass({ apiSource, apiKey, passId })
      if (!active) return
      if (result.ok === false || !result.pass) return navigate("/templates")

      setPasses(current => {
        const idx = current.findIndex(item => item.template_id === templateId && item.pass_id === passId)
        if (idx < 0) return [...current, result.pass]
        const next = [...current]
        next[idx] = result.pass
        return next
      })
      setPass(result.pass)
    }

    loadPass()
    return () => { active = false }
  }, [apiKey, apiSource, navigate, passId, setPasses, templateId])

  const setLocationStatePass = updatedPass => {
    setPass(updatedPass)
    setPasses(current => {
      const idx = current.findIndex(item => item.template_id === templateId && item.pass_id === passId)
      if (idx < 0) return [...current, updatedPass]
      const next = [...current]
      next[idx] = updatedPass
      return next
    })
    navigate(location.pathname, { replace: true, state: { ...location.state, pass: updatedPass } })
  }

  const loadedPass = pass?.pass_id === passId ? pass : undefined

  if (template?.managed_pass_template === 1) return (
    <ManagePassSubpageLayout title={`Pass Actions (${pass?.pass_id})`} HelpText={HelpText} heading={`${loadedPass?.pass_id ?? ''} ${loadedPass?.mode === "test" ? " [test]" : ""}`} template={template} pass={loadedPass} onClose={() => navigate(`/templates/${templateId}/passes`)}>
      <div className='h-full min-h-0 w-full'>
        <SmartixScannerEditForm pass={loadedPass} template={template} setPass={setLocationStatePass} />
      </div>
    </ManagePassSubpageLayout>
  )
}

export default PassScanActions


const SmartixScannerEditForm = ({ pass, template, setPass }) => {

  if (pass && template)
    switch (template?.pass_type_id) {
      case "managed_gift": return <GiftcardEditor pass={pass} template={template} setPass={setPass} />
      case "managed_stamp": return <StampcardEditor pass={pass} template={template} setPass={setPass} />
      case "managed_event": return <EventTicketEditor pass={pass} template={template} setPass={setPass} />
      case "managed_event_bg": return <EventTicketEditor pass={pass} template={template} setPass={setPass} />
      case "managed_loyalty": return <LoyaltyEditor pass={pass} template={template} setPass={setPass} />
      case "managed_membership": return <MembershipEditor pass={pass} template={template} setPass={setPass} />
      case "managed_membership_sc": return <MembershipEditor pass={pass} template={template} setPass={setPass} />
    }
}
